import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import {  Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, Subscription, combineLatest, forkJoin, map, take, takeUntil } from 'rxjs';
import { AuthService } from '@core/auth.service';
import * as fromUserProfile from '@modules/shared/user-profile.reducer';
import { CommonService } from '@shared/services/common.service';
import { CoreService } from '@services/core.service';
import { Application } from '@enums/application.enums';
import { DOCUMENT } from '@angular/common';
import {
  Fields,
  SIDE_BAR_MODULE,
  VERIFICATION_STATUS
} from '@constants/constant';
import { BsModalService } from 'ngx-bootstrap/modal';
import { InfoModalComponent } from '@components/info-modal/info-modal.component';
import { ApplicationStatusEnums, CommonEnums, PERMISSION_TYPE } from '@shared/enums/common.enum';
import { PERMISSION_KEYS } from '@shared/constants/permissions.contant';
import { environment } from '@environments/environment';
import { appRoutingUrl } from '@enums/comman.enums';
import { PwaService } from '@core/pwa.service';
import { WhiteLabelConfig } from '@models/common.interface';
import { PermissionService } from '@shared/services/permissions.service';
import { ResourcesService } from '@modules/resources/services/resources-service';
import { ROUTE_PATHS } from '@constants/routes';
import { SIDEBAR_TABS } from '@shared/constants/tab-approval.constant';
import { MobileBridgeService } from '@services/mobile-bridge.service';
import { CREATORS_CONST } from '@modules/creators-home/constants/creatorsConstants';
import { POST_TYPE } from '@modules/dashboard/constants/home-strings.constant';
import { InsightsStateService } from '@modules/insights/services/insights-state.service';
import { INSIGHT_TAB, USER_ACTIONS } from '@modules/insights/components/constant';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  @ViewChild('creatorRequirementsModal') creatorRequirementsModal;
  userProfile: any;
  isSidebarLoading: boolean = true;
  readonly subscriptionList: Subscription[] = [];
  whiteLabelConfigData: any;
  isWhiteLabel: boolean;
  userBadges = {
    messageCount: '',
    unreadActivityCount: '',
    my_fulfillment_requests_count: '',
    refund_requests_count: '',
    merchandise_awaiting_fan_confirmation_count: '',
    experience_awaiting_fan_confirmation_count: ''
  };
  isSticky = false;
  private scrollTicking = false;
  private readonly STICKY_HYSTERESIS = 50; // Buffer to prevent flickering
  applicationStatusEnums = ApplicationStatusEnums;
  successModalField: any;
  pendingModalField: any;
  failureModalField: any;
  applicationRejectModalField: any;
  permissionKeys = PERMISSION_KEYS;
  isCreatorRequestResubmitted: boolean = false;
  PERMISSION_TYPE = PERMISSION_TYPE;
  commonEnums = CommonEnums;
  whiteLabelConfig: WhiteLabelConfig;
  walletRoute: string = '/wallet/my-purchases';
  totalResourcesCount: number = 0;
  appRoutingUrl = appRoutingUrl;
  destroy$ = new Subject<void>();
  Fields = Fields;
  ROUTES = ROUTE_PATHS
  CREATORS_CONST = CREATORS_CONST;
  readonly postType = POST_TYPE;
  SIDEBAR_TABS = SIDEBAR_TABS;
  
  constructor(
    private readonly router: Router,
    private readonly store: Store<fromUserProfile.UserProfileState>,
    public readonly authService: AuthService,
    private readonly modalService: BsModalService,
    public cs: CommonService,
    private readonly coreService: CoreService,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    public pwaService: PwaService,
    public ps: PermissionService,
    public resourcesService: ResourcesService,
    public mobileBridgeService:MobileBridgeService,
    private readonly insightsStateService: InsightsStateService
  ) {
    const config = this.cs.getWhiteLabelConfiguration();
    if (config) {
      this.whiteLabelConfigData = config.user;
      this.whiteLabelConfig = config.data.whiteLabel;
      this.isWhiteLabel = true;
    }
  }

  ngOnInit(): void {
      if (this.authService.isAuthenticated()) {
        this.isSidebarLoading = true;
        this.getWalletRoute();
        this.getBadges();
        this.userProfileSubscription();
        this.resourcesService.fetchResourcesCount();
      } else {
        this.isSidebarLoading = false;
      }
      this.getMessageCount();

      const resourcesSub = this.cs.totalUnreadResourcesCountSubject
        .pipe(takeUntil(this.destroy$))
        .subscribe((count) => {
          this.totalResourcesCount = count;
        });
      this.subscriptionList.push(resourcesSub);
  }
  
  get isLoggedIn(): boolean {
    return this.authService.isLogin;
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event: any) {
    if (this.cs.isBrowser && !this.scrollTicking) {
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.updateStickyState(event);
        this.scrollTicking = false;
      });
    }
  }

  private updateStickyState(event: any): void {
    const scrollTop = event.target.scrollingElement.scrollTop;
    const footer: any = this.document.querySelector('.footer');
    const sidebar: any = this.document.querySelector('.sidebar');

    if (footer && sidebar) {
      const footerOffset = footer.offsetTop;
      const sidebarHeight = sidebar.clientHeight;
      const maxOffset = footerOffset - sidebarHeight;
      const currentScrollPosition = scrollTop + Application.TWENTY*10;

      // Use hysteresis to prevent flickering near the threshold
      if (this.isSticky) {
        // When already sticky, only switch off when scrolled significantly above threshold
        if (currentScrollPosition < maxOffset - this.STICKY_HYSTERESIS) {
          this.isSticky = false;
        }
      } else {
        // When not sticky, only switch on when scrolled significantly past threshold
        if (currentScrollPosition > maxOffset + this.STICKY_HYSTERESIS) {
          this.isSticky = true;
        }
      }
    } else {
      this.isSticky = false;
    }

    if (!this.isSticky) {
      this.renderer?.removeClass(
        this.document.querySelector('.main-content'),
        'remove-left-margin'
      );
    } else {
      this.renderer?.addClass(
        this.document.querySelector('.main-content'),
        'remove-left-margin'
      );
    }
  }
  getWalletRoute() {
    const permissionRoutes: { [key: string]: string } = {
      VIEW_MY_WALLET_BALANCE: '/wallet',
      ADD_FUND_TO_THE_WALLET: '/wallet',
      WITHDRAW_MONEY_TO_MY_BANK_ACCOUNT: '/wallet',
      ADD_REMOVE_BANK_ACCOUNT_AND_CREDIT_CARD: '/wallet',
      VIEW_TRANSACTION_HISTORY: '/wallet/transaction-history'
    };

    const permissionChecks = Object.entries(permissionRoutes).map(
      ([key, route]) => {
        return this.ps
          .hasPermission(this.permissionKeys[key], PERMISSION_TYPE.READ, true)
          .pipe(map((hasPermission) => (hasPermission ? route : null)));
      }
    );

    combineLatest(permissionChecks)
      .pipe(
        map(
          (routes) =>
            routes.find((route) => route !== null) || '/wallet/my-purchases'
        )
      )
      .subscribe((finalRoute) => {
        this.walletRoute = finalRoute;
      });
  }
  userProfileSubscription(): void {
    const sub = this.store
      .select(fromUserProfile.getUserProfileItems)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        const profile = res as any;
        const isProfileReady = !!(profile?.gsi4pk || profile?.user_name || profile?.id);
        if (isProfileReady) {
          this.isSidebarLoading = false;
        }
        this.userProfile = res;
        this.userProfile = {
          ...this.userProfile,
          isAbove18: this.cs.isUser18Above(this.userProfile.birth_date)
        };
        this.isCreatorRequestResubmitted =
          this.userProfile.id_verification_status ===
          this.applicationStatusEnums.RESUBMIT;
        this.setValues();
      });

    this.subscriptionList.push(sub);
  }


  get isEditPreferencePage(): boolean {
    return this.router.url.startsWith('/edit-preference');
  }

  getBadges() {
    const sub = this.store
      .select(fromUserProfile.getRefreshStateItems)
      .subscribe((res: any) => {
        if (Object.keys(res).length) {
          this.userBadges = {
            messageCount: res?.unreadMessage
              ? this.cs.kFormatter(res?.unreadMessage)
              : '',
            unreadActivityCount: res?.unread_activity_count,
            my_fulfillment_requests_count: res?.my_fulfillment_requests_count,
            refund_requests_count: res?.refund_requests_count,
            merchandise_awaiting_fan_confirmation_count:
              res?.merchandise_awaiting_fan_confirmation_count,
            experience_awaiting_fan_confirmation_count:
              res?.experience_awaiting_fan_confirmation_count
          };
          this.coreService.messageCount.next(
            res?.unreadMessage ? res?.unreadMessage : ''
          );
        }
      });
    this.subscriptionList.push(sub);
  }

  getRouteStatus() {
    return this.router.url.includes('current-balance-home');
  }
  get getRouteStatusForMonetize() {
    return this.router.url.includes(SIDE_BAR_MODULE.MONETIZE);
  }
  get getRouteStatusForResources() {
    return this.router.url.includes(SIDE_BAR_MODULE.RESOURCES);
  }

  getRouteStatusForWallet() {
    return this.router.url.includes('/wallet');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptionList.forEach((sub) => sub?.unsubscribe());
  }

  getMessageCount() {
    const sub = this.coreService.messageCount.subscribe((res: any) => {
      this.userBadges.messageCount = res ? this.cs.kFormatter(res) : '';
    });
    this.subscriptionList.push(sub);
  }

  resetCreatorSubTabs() {
    this.cs.activeCreatorTab.next('Creators');
    this.cs.creatorSubTab.next('following');
  }

  get isCreatorRequestPending() {
    return (
      this.userProfile?.admin_approval_status ===
      this.applicationStatusEnums.PENDING
    );
  }

  get isCreatorRequestFailed() {
    return (
      this.userProfile?.admin_approval_status ===
      this.applicationStatusEnums.FAILED
    );
  }

  get isCreatorRequestApproved() {
    return (
      this.userProfile?.admin_approval_status ===
      this.applicationStatusEnums.APPROVED
    );
  }

  navigateToBecomeACreatorPage() {
    if (this.isCreatorRequestResubmitted) {
      this.router.navigate([appRoutingUrl.BECOME_A_CREATOR]);
      return;
    }
    if (this.isCreatorRequestPending) {
      this.openVeriffStatusModals();
    }
    if (this.isCreatorRequestFailed) {
      this.openModal(this.applicationRejectModalField);
    } else if (!this.userProfile?.admin_approval_status) {
      this.creatorRequirementsModal?.openModal();
    }
  }

  setValues() {
    this.failureModalField = {
      icon: './assets/images/svg/red-info-blank.svg',
      heading: 'Identity Verification Failed',
      description: `Unfortunately, your identity was not able to be verified. <br>For this reason we can\'t evaluate your application to Become a Creator. <br> If you think this is a mistake, please contact <a href="${environment.websiteURL}/help-faq" target="_blank" class="highlight">Customer&nbsp;Support</a>.`,
      iconStyleClass: 'red-bg',
      buttonField: {
        type: 'button',
        class: 'btn btn-primary',
        label: 'Done'
      }
    };

    this.successModalField = {
      icon: './assets/images/svg/checkmark-outline.svg',
      heading: 'Your Identity Has Been Verified',
      description:
        'The TopFan team will now evaluate your application to Become a Creator and you will typically receive a status update by email within one business day. <br> You can now return to TopFan to continue exploring the community.',
      iconStyleClass: 'confirm-creator-icon-bg',
      buttonField: {
        type: 'button',
        class: 'btn btn-primary',
        label: 'Done'
      }
    };

    this.pendingModalField = {
      icon: './assets/images/svg/pending.svg',
      heading: 'Identity Verification Pending',
      description:
        'Your identity verification is currently in progress. We are evaluating your application to Become a Creator. Please be patient as this process may take some time. If you have any questions, please contact <a href="' +
        environment.websiteURL +
        'help-faq" target="_blank" class="highlight">Customer&nbsp;Support</a>.',
      iconStyleClass: 'yellow-bg',
      buttonField: {
        type: 'button',
        class: 'btn btn-primary',
        label: 'Done'
      }
    };

    this.applicationRejectModalField = {
      icon: './assets/images/svg/red-info-blank.svg',
      iconStyleClass: 'red-bg',
      heading: 'Your application has been declined by our team. ',
      description: `Reason: ${this.userProfile?.admin_action_reason}<br><br> If you think this is a mistake, please contact <a href="${environment.websiteURL}help-faq" target="_blank" class="highlight">Customer&nbsp;Support</a>.`
    };
  }

  openVeriffStatusModals() {
    const verification_status = this.userProfile?.id_verification_status;
    switch (verification_status) {
      case VERIFICATION_STATUS.APPROVED:
        this.openModal(this.successModalField);
        break;
      case VERIFICATION_STATUS.PENDING:
        this.openModal(this.pendingModalField);
        break;
      default:
        this.openModal(this.failureModalField);
        break;
    }
  }

  openModal(modalField: any) {
    this.modalService.show(InfoModalComponent, {
      initialState: modalField,
      class: 'modal-lg',
      backdrop: 'static'
    });
  }

  navigateToLiveStream() {
    if(this.userProfile?.is_live && this.userProfile?.livestream_id && this.userProfile?.user_name) {
      this.router.navigate([`dashboard/live-stream/${this.userProfile?.user_name}/${this.userProfile?.livestream_id}`]);
    } else {
      this.router.navigate(['/dashboard/live-stream']);
    }
  }

  setInsightsState(): void {
  // Collect permission observables
    forkJoin({
      earning: this.ps.hasPermission(this.permissionKeys.VIEW_EARNING_METRICS, PERMISSION_TYPE.READ, true).pipe(take(1)),
      engagement: this.ps.hasPermission(this.permissionKeys.VIEW_ENGAGEMENT_METRICS, PERMISSION_TYPE.READ, true).pipe(take(1)),
      segmentation: this.ps.hasPermission(this.permissionKeys.ACCESS_USER_SEGMENTATION, PERMISSION_TYPE.READ, true).pipe(take(1)),
      fanMgmt: this.ps.hasPermission(this.permissionKeys.FAN_MANAGEMENT, PERMISSION_TYPE.READ, true).pipe(take(1)),
    }).subscribe(perms => {
      let targetTab = INSIGHT_TAB.EARNING;

      if (perms.earning) {
        targetTab = INSIGHT_TAB.EARNING;
      } else if (perms.engagement) {
        targetTab = INSIGHT_TAB.ENGAGEMENT;
      } else if (perms.segmentation || perms.fanMgmt) {
        targetTab = INSIGHT_TAB.FAN_RELATIONSHIP_MANAGEMENT;
      }

      this.insightsStateService.tabType = targetTab;
      this.insightsStateService.frmTabType = INSIGHT_TAB.FAN_MANAGEMENT;
      this.insightsStateService.fanTabKey = INSIGHT_TAB.ALL;
      this.insightsStateService.fanManagementDateRange = null;
      this.insightsStateService.selectedFanFilter = USER_ACTIONS.ALL;
      this.router.navigate([ROUTE_PATHS.INSIGHTS_PATH], {
        queryParams: { tab: targetTab }
      });
    });
  }

  isActive(url: string): boolean {
    return this.router.url?.includes(url);
  }
}
